"use client";

import React, { useState, useCallback, useMemo } from "react";
import { ChevronDown, X } from "lucide-react";
import { Button } from "./button.js";
import { Checkbox } from "./checkbox.js";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./popover.js";

interface FacetValue {
  value: string;
  count: number;
}

interface SearchFilterPopoverProps {
  facets: {
    types?: FacetValue[];
    status?: FacetValue[];
    priority?: FacetValue[];
    tags?: FacetValue[];
  };
  selectedFilters: {
    types?: string[];
    status?: string[];
    priority?: string[];
    tags?: string[];
  };
  onFilterChange: (filters: any) => void;
  onClearFilters: () => void;
  trigger?: React.ReactNode;
}

const FILTER_LABELS: Record<string, string> = {
  types: "Type",
  status: "Status",
  priority: "Priority",
  tags: "Tags",
};

export function SearchFilterPopover({
  facets,
  selectedFilters,
  onFilterChange,
  onClearFilters,
  trigger,
}: SearchFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    types: true,
    status: true,
    priority: false,
    tags: false,
  });

  const toggleSection = useCallback((section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  }, []);

  const handleFilterToggle = useCallback(
    (filterType: string, value: string) => {
      const currentFilters = selectedFilters[filterType as keyof typeof selectedFilters] || [];
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter((v) => v !== value)
        : [...currentFilters, value];

      onFilterChange({
        ...selectedFilters,
        [filterType]: newFilters.length > 0 ? newFilters : undefined,
      });
    },
    [selectedFilters, onFilterChange]
  );

  const handleClear = useCallback(() => {
    onClearFilters();
    setOpen(false);
  }, [onClearFilters]);

  const hasActiveFilters = useMemo(() => {
    return Object.values(selectedFilters).some(
      (filters) => filters && filters.length > 0
    );
  }, [selectedFilters]);

  const filterSections = useMemo(() => {
    return Object.entries(facets).filter(([_, values]) => values && values.length > 0);
  }, [facets]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {trigger || (
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-blue-500 text-xs font-medium text-white">
                {Object.values(selectedFilters).reduce(
                  (sum, filters) => sum + (filters?.length || 0),
                  0
                )}
              </span>
            )}
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="start">
        <div className="flex flex-col max-h-96">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="font-semibold text-sm">Filters</h3>
            <button type="button"
              onClick={() => setOpen(false)}
              className="p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* Filter sections */}
          <div className="overflow-y-auto flex-1">
            {filterSections.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-gray-500">
                No filters available
              </div>
            ) : (
              filterSections.map(([filterType, values]) => (
                <div key={filterType} className="border-b border-gray-100 last:border-b-0">
                  {/* Section header */}
                  <button type="button"
                    onClick={() => toggleSection(filterType)}
                    className="w-full px-4 py-2 flex items-center justify-between hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-sm font-medium text-gray-900">
                      {FILTER_LABELS[filterType] || filterType}
                    </span>
                    <ChevronDown
                      className={`h-4 w-4 text-gray-400 transition-transform ${
                        expandedSections[filterType] ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {/* Section content */}
                  {expandedSections[filterType] && (
                    <div className="px-4 py-2 space-y-2 bg-gray-50">
                      {values.map((facet) => {
                        const isSelected = (
                          selectedFilters[filterType as keyof typeof selectedFilters] || []
                        ).includes(facet.value);

                        return (
                          <label
                            key={facet.value}
                            className="flex items-center gap-2 cursor-pointer hover:bg-gray-100 px-2 py-1 rounded transition-colors"
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() =>
                                handleFilterToggle(filterType, facet.value)
                              }
                            />
                            <span className="text-sm text-gray-700 flex-1">
                              {facet.value}
                            </span>
                            <span className="text-xs text-gray-500">
                              {facet.count}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {hasActiveFilters && (
            <div className="flex gap-2 px-4 py-3 border-t border-gray-200 bg-gray-50">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClear}
                className="flex-1"
              >
                Clear
              </Button>
              <Button
                size="sm"
                onClick={() => setOpen(false)}
                className="flex-1"
              >
                Apply
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
